import { Alert } from '@creditornot/cb-alert';
import { Button, IconButton } from '@creditornot/cb-button';
import { Minus, Plus, Trashbin } from '@creditornot/cb-icons';
import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { cloneDeep } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import TimePicker from 'common/components/weekly-schedules/TimePairInput/TimePicker';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';

import { InvalidScheduleError, Schedule, SchedulingFormValues } from '../types';
import { createSchedule, createScheduleTimeRange, getLocalizedWeekDay } from '../utils';

const StyledSection = styled.div`
  padding: 24px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const StyledSectionTitle = styled(Typography.Title1)`
  margin-block-end: 16px;
  display: block;
`;

const StyledSectionContent = styled.div`
  display: flex;
  align-content: center;
  align-items: center;
  gap: 8px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    display: grid;
    gap: 4px;
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StyledWeekday = styled.label`
  position: relative;
  flex: 1;
`;

const StyledWeekdayLabel = styled(Typography.Body3)`
  padding: 8px;
  border-radius: 8px;
  background-color: ${cssThemedValue({ light: colors.pepper4, dark: colors.salt4 })};
  color: ${cssThemedValue('textSecondary')};
  display: inline-block;
  width: 100%;
  text-align: center;
  cursor: pointer;
`;

const StyledHiddenInput = styled.input`
  width: 0;
  height: 0;
  opacity: 0;
  position: absolute;

  &:checked + ${StyledWeekdayLabel} {
    background-color: ${colors.wolt8};
    color: ${colors.wolt100};
  }

  &:disabled + ${StyledWeekdayLabel} {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-within + ${StyledWeekdayLabel} {
    outline: 4px solid ${colors.wolt24};
  }
`;

const StyledTimeRangesWrapper = styled.div`
  margin-block-start: 24px;
`;

const StyledTimeRange = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${cssThemedValue('textSecondary')};
`;

const StyledTimePicker = styled(TimePicker)`
  max-width: 80px;
`;

const StyledScheduleWrapper = styled.div`
  position: relative;
  border: 1px solid;
  border-color: ${cssThemedValue('borderPrimary')};
  border-radius: 8px;
  padding: 16px;

  &:not(:first-of-type) {
    margin-block-start: 16px;
  }

  &:last-of-type {
    margin-block-end: 16px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
`;

const StyledRemoveButton = styled(IconButton)`
  position: absolute;
  bottom: 16px;
  inset-inline-end: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    position: relative;
    bottom: unset;
    inset-inline-end: unset;
    margin-block-start: 16px;
  }
`;

const StyledErrorMessage = styled(Alert).attrs({ variant: 'error' })`
  background-color: transparent;
  color: ${colors.strawberry100};
  padding: 8px 0 0;
`;

export type SchedulesSectionProps = {
  errors?: InvalidScheduleError[];
  schedules?: Schedule[];
  onScheduleChange(schedules: SchedulingFormValues['schedules']): void;
  onTimeframeChange(timeframe: SchedulingFormValues['timeframe']): void;
};

const SchedulesSection = ({ schedules, errors, onScheduleChange, onTimeframeChange }: SchedulesSectionProps) => {
  const intl = useIntl();

  // FIXME: make it respect first day of week based on locale
  const weekdays = useMemo<{ [key in Weekday]: string }>(() => {
    return {
      monday: getLocalizedWeekDay(0, intl.locale),
      tuesday: getLocalizedWeekDay(1, intl.locale),
      wednesday: getLocalizedWeekDay(2, intl.locale),
      thursday: getLocalizedWeekDay(3, intl.locale),
      friday: getLocalizedWeekDay(4, intl.locale),
      saturday: getLocalizedWeekDay(5, intl.locale),
      sunday: getLocalizedWeekDay(6, intl.locale),
    };
  }, [intl.locale]);

  const handleWeekDayChange = (e: React.ChangeEvent<HTMLInputElement>, scheduleIdx: number) => {
    if (!schedules) {
      return;
    }

    const { checked: isChecked, value } = e.target;
    const currentSchedule = schedules[scheduleIdx];

    const newSchedule: Schedule = {
      selectedDays: isChecked
        ? [...currentSchedule.selectedDays, value]
        : currentSchedule.selectedDays.filter(day => day !== value),
      timeRanges: currentSchedule.timeRanges,
    };

    const newState = Object.assign([], schedules, { [scheduleIdx]: newSchedule });
    onScheduleChange(newState);
  };

  const handleChangeTimeRange = (value: number, name: 'from' | 'to', scheduleIdx: number, timeRangeIdx: number) => {
    if (!schedules) {
      return;
    }

    const newState = cloneDeep(schedules);
    const currentSchedule = newState[scheduleIdx];

    currentSchedule.timeRanges[timeRangeIdx][name] = value;

    onScheduleChange(newState);
  };

  const handleRemoveTimeRange = async (scheduleIdx: number, timeRangeIdx: number) => {
    if (!schedules) {
      return;
    }

    const currentSchedule = schedules[scheduleIdx];
    const newTimeRanges = currentSchedule.timeRanges.filter((_, _timeRangeIdx) => _timeRangeIdx !== timeRangeIdx);

    if (newTimeRanges.length > 0) {
      const newSchedule: Schedule = {
        selectedDays: currentSchedule.selectedDays,
        timeRanges: newTimeRanges,
      };
      const newState = Object.assign([], schedules, { [scheduleIdx]: newSchedule });

      onScheduleChange(newState);
    } else {
      handleRemoveSchedule(scheduleIdx);
    }
  };

  const handleAddTimeRange = (scheduleIdx: number) => {
    if (!schedules) {
      return;
    }

    const currentSchedule = schedules[scheduleIdx];

    const lastTimeRange = currentSchedule.timeRanges[currentSchedule.timeRanges.length - 1];
    const newSchedule = createSchedule({
      selectedDays: currentSchedule.selectedDays,
      timeRanges: [
        ...currentSchedule.timeRanges,
        createScheduleTimeRange({
          from: lastTimeRange.to,
          to: lastTimeRange.to + 60,
        }),
      ],
    });

    const newState = Object.assign([], schedules, { [scheduleIdx]: newSchedule });
    onScheduleChange(newState);
  };

  const handleAddSchedule = () => {
    if (!schedules) {
      return;
    }

    const selectedDays = schedules.reduce((acc, schedule) => {
      return [...acc, ...schedule.selectedDays];
    }, []);
    const availableWeekdays = Object.keys(weekdays).filter(weekday => !selectedDays.includes(weekday));

    const newSchedule = createSchedule({
      selectedDays: availableWeekdays ?? [],
    });
    const newState: Schedule[] = [...schedules, newSchedule];

    onScheduleChange(newState);
  };

  const handleRemoveSchedule = (scheduleIdx: number) => {
    if (!schedules) {
      return;
    }

    const isLastSchedule = schedules.length === 1;
    const newState: Schedule[] = schedules.filter((_, idx) => idx !== scheduleIdx);

    if (isLastSchedule) {
      onTimeframeChange('open-hours');
    }

    onScheduleChange(newState);
  };

  useEffect(() => {
    if (!schedules?.length) {
      onScheduleChange([createSchedule({ selectedDays: Object.keys(weekdays) })]);
    }
  }, [schedules, onScheduleChange, weekdays]);

  return (
    <StyledSection>
      <StyledSectionTitle>
        <FormattedMessage id="campaigns.form.scheduling.schedules.label" />
      </StyledSectionTitle>

      {schedules?.map((schedule, scheduleIdx) => {
        const scheduleError: InvalidScheduleError | undefined = errors?.find(
          (err: InvalidScheduleError) => err.scheduleIdx === scheduleIdx && !err.hasOwnProperty('timeRangeIdx'),
        );

        return (
          <StyledScheduleWrapper key={scheduleIdx} data-testid="schedule">
            <StyledSectionContent>
              {Object.entries(weekdays).map(([key, label]: [Weekday, string]) => {
                const isDisabled = schedules.some((schedule, i) => {
                  if (i === scheduleIdx) {
                    return false;
                  }

                  return schedule.selectedDays.includes(key);
                });

                return (
                  <StyledWeekday key={key}>
                    <StyledHiddenInput
                      name="weekday"
                      type="checkbox"
                      value={key}
                      checked={schedule.selectedDays.includes(key)}
                      disabled={isDisabled}
                      onChange={e => handleWeekDayChange(e, scheduleIdx)}
                    />
                    <StyledWeekdayLabel>{label}</StyledWeekdayLabel>
                  </StyledWeekday>
                );
              })}
            </StyledSectionContent>
            {scheduleError && (
              <StyledErrorMessage data-testid="schedule__weekday-error">
                <FormattedMessage id={scheduleError.id} />
              </StyledErrorMessage>
            )}

            {schedule.timeRanges.map((timeRange, timeRangeIdx) => {
              const timeRangeError = errors?.find((err: InvalidScheduleError) => {
                return err.scheduleIdx === scheduleIdx && err.timeRangeIdx === timeRangeIdx;
              });

              return (
                <StyledTimeRangesWrapper key={timeRangeIdx} data-testid="schedule__time-ranges-row">
                  <StyledTimeRange>
                    <StyledTimePicker
                      value={timeRange.from}
                      onChange={value => handleChangeTimeRange(value, 'from', scheduleIdx, timeRangeIdx)}
                    />
                    &#8211;
                    <StyledTimePicker
                      value={timeRange.to}
                      onChange={value => handleChangeTimeRange(value, 'to', scheduleIdx, timeRangeIdx)}
                    />
                    <IconButton
                      size="tiny"
                      variant="red"
                      onClick={() => handleRemoveTimeRange(scheduleIdx, timeRangeIdx)}
                    >
                      <Minus />
                    </IconButton>
                    {timeRangeIdx === schedule.timeRanges.length - 1 && (
                      <IconButton size="tiny" variant="blue" onClick={() => handleAddTimeRange(scheduleIdx)}>
                        <Plus />
                      </IconButton>
                    )}
                  </StyledTimeRange>
                  {timeRangeError && (
                    <StyledErrorMessage data-testid="schedule__time-range-error">
                      <FormattedMessage id={timeRangeError.id} />
                    </StyledErrorMessage>
                  )}
                </StyledTimeRangesWrapper>
              );
            })}

            <StyledRemoveButton
              size="small"
              variant="red"
              onClick={() => handleRemoveSchedule(scheduleIdx)}
              data-testid="schedule__remove-schedule-button"
            >
              <Trashbin />
            </StyledRemoveButton>
          </StyledScheduleWrapper>
        );
      })}

      <Button variant="lightBlue" size="small" onClick={handleAddSchedule} data-testid="schedule__add-schedule-button">
        <FormattedMessage id="campaigns.form.scheduling.schedules.add-schedule" />
      </Button>
    </StyledSection>
  );
};

export default SchedulesSection;
